import { Box, Grid, Pagination, Typography } from "@mui/material";
import DashboardTabs from "components/dashboard/dashboard-tabs";
import BookingDocumentItem from "components/documents/booking-document-item";
import { ROUTES } from "constants/routes";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAllDocuments } from "slices/documentsReducer";
import { useAppDispatch, useAppSelector } from "store/appHooks";
import { DownloadFile } from "utils/download.util";
import { getPaginationCountText } from "utils/messages.util";
import useQuery from "utils/router.util";

const DocumentsPage = () => {
  const { documentsReducer } = useAppSelector((state) => state);
  const { list, listMeta, ownerResources, insuranceBookingForms }: any =
    documentsReducer;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const query = useQuery();
  const qPage = query.get("page") || 1;

  useEffect(() => {
    dispatch(fetchAllDocuments({ page: Number(qPage) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qPage]);

  const redirectWithQuery = (value: string) => {
    var searchParams = new URLSearchParams(window.location.search);
    searchParams.set("page", value.toString());
    navigate(`${ROUTES.DOCUMENT}?${searchParams}`);
  };

  return (
    <Box>
      <DashboardTabs tab="documents" />
      <Box
        sx={{
          display: {
            md: "flex",
            lg: "flex",
            xl: "flex",
            sm: "none",
            xs: "none",
          },
          px: "4%",
          py: "20px",
          borderBottom: "1px solid #F4F4F4",
        }}
      >
        <Typography
          sx={{
            color: "#323232",
            fontWeight: 600,
            fontSize: "24px",
            lineHeight: "35px",
          }}
        >
          Documents
        </Typography>
      </Box>

      <Box sx={{ px: "4%", pt: "40px", pb: "20px" }}>
        {list.length > 0 && (
          <Typography
            sx={{
              color: "#323232",
              fontWeight: 600,
              fontSize: {
                md: "22px",
                lg: "22px",
                xl: "22px",
                sm: "16px",
                xs: "16px",
              },
              lineHeight: "26px",
            }}
          >
            Booking Contracts
          </Typography>
        )}

        {list.length === 0 && (
          <Box
            sx={{
              mx: "4%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              width: "100%",
              my: "8%",
            }}
          >
            <img
              src="/assets/img/icons/document_empty.svg"
              alt=""
              width="212px"
            />
            <Typography
              sx={{
                color: "#323232",
                fontWeight: 700,
                fontSize: "26px",
                lineHeight: "31px",
                mt: "20px",
                mb: "10px",
                textAlign: "center",
              }}
            >
              No Booking Documents Found!
            </Typography>
            <Typography
              sx={{
                color: "#A2A2A2",
                fontWeight: 500,
                fontSize: "16px",
                lineHeight: "24px",
                mb: "15px",
                textAlign: "center",
              }}
            >
              It seems that there are no documents available for your bookings
              at the moment. Please check back later for any updates.
            </Typography>
          </Box>
        )}
        <Grid container spacing={2} mt="10px">
          {list.map((d: any) => (
            <Grid item md={4} lg={4} xl={4} sm={12} xs={12}>
              <BookingDocumentItem item={d} />
            </Grid>
          ))}
        </Grid>
        {list?.length > 0 && (
          <Box
            sx={{
              display: "flex",
              py: "20px",
              justifyContent: "space-between",
              alignItems: "center",

              flexWrap: "wrap",
              gap: "20px",
            }}
          >
            <Box>
              <Typography
                sx={{ color: "#A2A2A2", fontSize: "13px", fontWeight: 600 }}
              >
                {getPaginationCountText(
                  query.get("page") ? Number(query.get("page")) : 1,
                  listMeta?.totalPages,
                  listMeta?.totalRecords,
                  6
                )}
              </Typography>
            </Box>
            <Pagination
              count={listMeta?.totalPages}
              shape="rounded"
              page={query.get("page") ? Number(query.get("page")) : 1}
              onChange={(_, page) => {
                redirectWithQuery(page.toString());
              }}
              color="primary"
            />
          </Box>
        )}
      </Box>

      {ownerResources.length > 0 && (
        <Box sx={{ px: "4%", pt: "10px", pb: "20px" }}>
          <Typography
            sx={{
              color: "#323232",
              fontWeight: 600,
              fontSize: {
                md: "22px",
                lg: "22px",
                xl: "22px",
                sm: "16px",
                xs: "16px",
              },
              lineHeight: "26px",
            }}
          >
            Owner Resources
          </Typography>
          <Grid container spacing={2} mt="10px">
            {ownerResources.map((d: any) => (
              <Grid item md={4} lg={4} xl={4} sm={12} xs={12}>
                <Box
                  sx={{
                    border: "1px solid #E2E2E2",
                    borderRadius: "10px",
                    padding: "20px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: "20px",
                        alignItems: "center",
                      }}
                    >
                      <img
                        src="/assets/img/icons/pdf_ic.svg"
                        alt=""
                        width="36"
                        height="48"
                      />

                      <Box>
                        <Typography
                          sx={{
                            color: "#383838",
                            fontSize: "15px",
                            fontWeight: 500,
                            lineHeight: "22px",
                          }}
                        >
                          {d.file_name}
                        </Typography>
                        <Typography
                          sx={{
                            color: "#A2A2A2",
                            fontSize: "13px",
                            fontWeight: 400,
                            lineHeight: "22px",
                          }}
                        >
                          {d.pages} pages • {d.size} • PDF
                        </Typography>
                      </Box>
                    </Box>
                    <img
                      src="/assets/img/icons/arrow_down_cr.svg"
                      alt=""
                      width="36"
                      height="48"
                      onClick={() => DownloadFile(d.file_url, d.file_name)}
                    />
                  </Box>
                  <Typography>Uploaded on {d.uploaded_on}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {insuranceBookingForms.length > 0 && (
        <Box sx={{ px: "4%", py: "30px" }}>
          <Typography
            sx={{
              color: "#323232",
              fontWeight: 600,
              fontSize: {
                md: "22px",
                lg: "22px",
                xl: "22px",
                sm: "16px",
                xs: "16px",
              },
              lineHeight: "26px",
            }}
          >
            Insurance and Booking Forms
          </Typography>
          <Grid container spacing={2} mt="10px">
            {insuranceBookingForms.map((d: any) => (
              <Grid item md={4} lg={4} xl={4} sm={12} xs={12}>
                <Box
                  sx={{
                    border: "1px solid #E2E2E2",
                    borderRadius: "10px",
                    padding: "20px 20px 10px 20px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: "20px",
                        alignItems: "center",
                      }}
                    >
                      <img
                        src="/assets/img/icons/pdf_ic.svg"
                        alt=""
                        width="36"
                        height="48"
                      />

                      <Box>
                        <Typography
                          sx={{
                            color: "#383838",
                            fontSize: "15px",
                            fontWeight: 500,
                            lineHeight: "22px",
                          }}
                        >
                          {d.file_name}
                        </Typography>
                        <Typography
                          sx={{
                            color: "#A2A2A2",
                            fontSize: "13px",
                            fontWeight: 400,
                            lineHeight: "22px",
                          }}
                        >
                          {d.pages} pages • {d.size} • PDF
                        </Typography>
                      </Box>
                    </Box>
                    <img
                      src="/assets/img/icons/arrow_down_cr.svg"
                      alt=""
                      width="36"
                      height="48"
                      onClick={() => DownloadFile(d.file_url, d.file_name)}
                    />
                  </Box>
                  <Typography
                    sx={{
                      color: "#A2A2A2",
                      fontSize: "13px",
                      fontWeight: 400,
                      lineHeight: "22px",
                      mt: "10px",
                    }}
                  >
                    Uploaded on {d.uploaded_on}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default DocumentsPage;
